import React from "react";
import DialogDataComponent, {executeComponentCallback} from 'Core/components/DialogDataComponent';
import PropTypes from "prop-types";
import {connect} from 'react-redux';
import * as actions from "../actions";
import * as config from "../config";
import {ASYNC_DOWNLOAD_FORMAT, ASYNC_DOWNLOAD_FORMAT_EXT, ASYNC_DOWNLOAD_FORMATS, ASYNC_DOWNLOAD_TYPES} from '../const';
import {AsyncDownloadDialogDataObject} from './dataObjects';
import {getDateString} from 'Core/helpers/datetime';
import FormWrapper, {FormField} from 'Core/components/advanced/FormWrapper';
import {FORM_FIELD_LABEL_POSITION} from 'Core/components/advanced/FormWrapper/FormField';
import TextInput, {TEXT_INPUT_LABEL_POSITION} from 'Core/components/input/TextInput';
import SelectInput from 'Core/components/input/SelectInput';
import {cloneDeep, set} from 'lodash';
import {getString} from 'Core/helpers/data';
import {getGlobalActions} from 'Core/helpers/redux';
import {toValidFilename} from 'Core/helpers/string';

/**
 * Get all actions used by this component
 * @type {Object}
 */
const allActions = getGlobalActions(actions);

class AsyncDownloadDialog extends DialogDataComponent {
	constructor(props) {
		super(props, {
			data: new AsyncDownloadDialogDataObject(
				props.type,
				props.params,
				props.format || ASYNC_DOWNLOAD_FORMAT.EXCEL,
				props.fileName || ''
			),
		}, {
			translationPath: `${config.translationPath}.AsyncDownloadDialog`,
			domPrefix: 'async-download-dialog',
			disableLoad: true,
			optimizedUpdate: true,
			optimizedUpdateIgnoreProps: ['onClose', 'onChange', 'onSave', 'onDelete', ...Object.keys(allActions)],
		});
	}
	
	/** @inheritDoc */
	getDataToReturn() {
		const {type} = this.props;
		/** @type {AsyncDownloadDialogDataObject} */
		let dataToReturn = cloneDeep(this.state.data);
		
		if (getString(dataToReturn, 'fileName') === '') {
			set(
				dataToReturn, 
				'fileName', 
				this.t('downloadFilename', `${config.translationPath}.type.${type}`, '', {
					date: getDateString(new Date(), 'yyyy-MM-dd')
				})
			);
		}
		
		return dataToReturn;
	}
	
	/** @inheritDoc */
	save() {
		const {startAsyncDownloadAction, addSuccessMessageAction} = this.props;
		const {dialogGUIID} = this.props;
		/** @type {AsyncDownloadDialogDataObject} */
		const data = this.getDataToReturn();

		// Do the validation
		const isValid = this.validate();

		// If validation is successful
		if (isValid) {
			this.executeAbortableAction(
				startAsyncDownloadAction,
				data.type,
				data.fileName,
				data.format,
				data.params, 
				'#async-download-dialog'
			)
				.then(() => addSuccessMessageAction(this.t('requestSentMsg', config.translationPath)))
				// Trigger component's onSave event with component's return data as param
				.then(() => executeComponentCallback(this.props.onSave, data, dialogGUIID));
			
		}
	}

	render() {
		const {type, format} = this.props;
		const title = (this.getProp('title') || this.t('title', `${config.translationPath}.type.${type}`));
		const saveBtnIcon = this.getProp('saveBtnIcon') || 'download';
		const saveBtnLabel = this.getProp('saveBtnLabel') || this.t('startDownloadBtnLabel');
		
		return this.renderDialog(
			this.renderTitle(title),
			(
				<FormWrapper className={this.getOption('domPrefix')}>
					<br />
					<FormField
						labelPosition={FORM_FIELD_LABEL_POSITION.STACKED}
						label={
							(!!format ? `${this.t(format, `${config.translationPath}.format`)} `: '') +
							this.t('fileNameField', '', (!!format ? 'l' : ''))
						}
					>
						<TextInput
							name="fileName"
							value={this.getValue('fileName')}
							onChange={e => this.handleValueChange('fileName', toValidFilename(e.target.value))}
							placeholder={this.t('downloadFilename', `${config.translationPath}.type.${type}`, '', {
								date: getDateString(new Date(), 'yyyy-MM-dd')
							})}
							showLabel={!!format}
							labelPosition={TEXT_INPUT_LABEL_POSITION.RIGHT}
							label={`.${ASYNC_DOWNLOAD_FORMAT_EXT[format]}`}
							labelWidth="auto"
						/>
					</FormField>
					{
						!format ?
							<FormField
								labelPosition={FORM_FIELD_LABEL_POSITION.STACKED}
								label={this.t('fileFormatField')}
							>
								<SelectInput
									simpleValue={true}
									isClearable={false}
									isSearchable={false}
									options={ASYNC_DOWNLOAD_FORMATS.map(f => ({
										label: this.t(f, `${config.translationPath}.format`),
										value: f
									}))}
									value={this.getValue('format')}
									onChange={v => this.setValue('format', v)}
								/>
							</FormField>
							: null
					}
				</FormWrapper>
			),
			{saveIcon: saveBtnIcon, saveLabel: saveBtnLabel}
		);
	}
}

/**
 * Define the component's own props that can be passed to it by parent components
 */
AsyncDownloadDialog.propTypes = {
	// Unique GUI ID of the dialog
	// @note This is automatically sent by the global Dialog component.
	dialogGUIID: PropTypes.string,
	// Dialog options
	// @note This is automatically sent by the global Dialog component.
	dialogOptions: PropTypes.object,
	// Action used to close the dialog
	// @note This is automatically sent by the global Dialog component.
	dialogCloseAction: PropTypes.func,
	
	// Type of the download to perform.
	type: PropTypes.oneOf(ASYNC_DOWNLOAD_TYPES).isRequired,
	// Filter parameters to pass to the download function.
	params: PropTypes.object,
	// Format of the downloaded file. If not provided, the default format (EXCEL) will be used.
	format: PropTypes.oneOf(ASYNC_DOWNLOAD_FORMATS),
	// File name to use for the downloaded file. If not provided, a default name will be used combining the download type
	// and the current date.
	fileName: PropTypes.string,
	// Icon of the save button to override the default one
	saveBtnIcon: PropTypes.string,
	// Label of the button to override the default one
	saveBtnLabel: PropTypes.string,

	// Events
	onSave: PropTypes.func,
	onClose: PropTypes.func,
};

export * from './dataObjects';
export default connect(null, allActions, null, {forwardRef: true})(AsyncDownloadDialog);